import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useDebounce } from '../../../hooks/useDebounce';
import {
  FETCH_MODEL_INSTANCES_CURSOR_PAGINATION_KEY,
  fetchModelInstancesCursorPaginationApi,
} from '../../hooks/api/useFetchModelInstancesCursorPagination';
import {
  appendToLevel,
  buildLevelFilters,
  buildSearchFilters,
  mapInstanceToNode,
  parseParentKey,
  setNodeChildren,
  type IAsyncTreeNode,
  type INodeConfig,
  type IOptionMeta,
} from './treeUtils';

interface IParams {
  modelIds?: string[];
  scopeFilter?: any;
  rootFilter?: any;
  transformer: string;
  childKey?: string;
  parentKey?: string;
  hasChildrenKey?: string;
  selectableExpression?: string;
  leavesOnly: boolean;
  isMultiple: boolean;
  pageSize?: number;
  searchableFields?: string[];
}

/**
 * Encapsulates the lazy tree loading: fetch roots, fetch a node's children on
 * expand, paginate each level via "load more" sentinels, and run a flat search.
 * Keeps FormReferenceTreeSelectAsync purely presentational.
 */
export const useAsyncReferenceTree = (params: IParams) => {
  const {
    modelIds,
    scopeFilter,
    rootFilter,
    transformer,
    childKey,
    parentKey,
    hasChildrenKey,
    selectableExpression,
    leavesOnly,
    isMultiple,
    pageSize,
    searchableFields,
  } = params;

  const queryClient = useQueryClient();

  const [browseTree, setBrowseTree] = useState<IAsyncTreeNode[]>([]);
  const [searchTree, setSearchTree] = useState<IAsyncTreeNode[]>([]);
  const [isLoadingRoots, setIsLoadingRoots] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 400);
  const isSearching = !!debouncedSearch;

  // id -> { modelId, version, label, meta } for building the submitted value.
  const optionByIdRef = useRef<Map<string, IOptionMeta>>(new Map());
  // Node values whose children have already been fetched (guards re-fetch).
  const loadedKeysRef = useRef<Set<string>>(new Set());

  const { arrayField: parentArrayField, field: parentField } = useMemo(
    () => parseParentKey(parentKey),
    [parentKey]
  );

  const nodeCfg = useMemo<INodeConfig>(
    () => ({
      transformer,
      childKey,
      hasChildrenKey,
      selectableExpression,
      leavesOnly,
      isMultiple,
    }),
    [
      transformer,
      childKey,
      hasChildrenKey,
      selectableExpression,
      leavesOnly,
      isMultiple,
    ]
  );

  const mapNodes = useCallback(
    (instances: any[]): IAsyncTreeNode[] =>
      (instances || []).map(inst => {
        const { node, meta } = mapInstanceToNode(inst, nodeCfg);
        optionByIdRef.current.set(node.value, meta);
        return node;
      }),
    [nodeCfg]
  );

  const runQuery = useCallback(
    (filters: string | undefined, page: number) => {
      const queryParams = { modelIds, filters, size: pageSize, page };
      return queryClient.fetchQuery({
        queryKey: [FETCH_MODEL_INSTANCES_CURSOR_PAGINATION_KEY, queryParams],
        queryFn: () => fetchModelInstancesCursorPaginationApi(queryParams),
        staleTime: 5 * 60 * 1000,
      });
    },
    [queryClient, modelIds, pageSize]
  );

  const fetchLevel = useCallback(
    (parentTermId: string | null, page: number) =>
      runQuery(
        buildLevelFilters({
          parentTermId,
          scopeFilter,
          rootFilter,
          parentArrayField,
          parentField,
        }),
        page
      ),
    [runQuery, scopeFilter, rootFilter, parentArrayField, parentField]
  );

  // makeSentinel <-> loadMore are mutually recursive; break the cycle with a ref.
  const loadMoreRef = useRef<
    (
      parentValue: string | null,
      parentTermId: string | null,
      page: number
    ) => void
  >(() => {});

  const makeSentinel = useCallback(
    (
      parentValue: string | null,
      parentTermId: string | null,
      page: number
    ): IAsyncTreeNode => ({
      value: `__more__:${parentValue ?? 'root'}:${page}`,
      __loadMore: true,
      parentValue,
      parentTermId,
      page,
      isLeaf: true,
      selectable: false,
      checkable: false,
      title: (
        <a
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            loadMoreRef.current(parentValue, parentTermId, page);
          }}
        >
          Load more…
        </a>
      ),
    }),
    []
  );

  const loadMore = useCallback(
    async (
      parentValue: string | null,
      parentTermId: string | null,
      page: number
    ) => {
      const { data, hasNext } = await fetchLevel(parentTermId, page);
      const newNodes = mapNodes(data);
      setBrowseTree(prev =>
        appendToLevel(
          prev,
          parentValue,
          () => makeSentinel(parentValue, parentTermId, page + 1),
          newNodes,
          hasNext
        )
      );
    },
    [fetchLevel, mapNodes, makeSentinel]
  );
  loadMoreRef.current = loadMore;

  const loadData = useCallback(
    async (node: any) => {
      if (node.__loadMore) return;
      const value = node.value as string;
      const termId = (node.termId as string) ?? null;
      if (loadedKeysRef.current.has(value)) return;
      loadedKeysRef.current.add(value);

      const { data, hasNext } = await fetchLevel(termId, 1);
      const newNodes = mapNodes(data);
      const children = hasNext
        ? [...newNodes, makeSentinel(value, termId, 2)]
        : newNodes;
      setBrowseTree(prev => setNodeChildren(prev, value, children));
    },
    [fetchLevel, mapNodes, makeSentinel]
  );

  // Load / reload roots whenever the model or scope changes.
  const resetKey = useMemo(
    () => JSON.stringify({ modelIds, scopeFilter, rootFilter }),
    [modelIds, scopeFilter, rootFilter]
  );
  const loadRootsRef = useRef<() => void>(() => {});
  loadRootsRef.current = async () => {
    if (!modelIds?.length) {
      setBrowseTree([]);
      return;
    }
    setIsLoadingRoots(true);
    loadedKeysRef.current = new Set();
    optionByIdRef.current = new Map();
    try {
      const { data, hasNext } = await fetchLevel(null, 1);
      const newNodes = mapNodes(data);
      setBrowseTree(
        hasNext ? [...newNodes, makeSentinel(null, null, 2)] : newNodes
      );
    } finally {
      setIsLoadingRoots(false);
    }
  };

  useEffect(() => {
    loadRootsRef.current();
  }, [resetKey]);

  // Flat search: query the list and show matches as leaves (no expansion).
  const searchableKey = useMemo(
    () => JSON.stringify(searchableFields ?? []),
    [searchableFields]
  );
  useEffect(() => {
    if (!debouncedSearch || !modelIds?.length) {
      setSearchTree([]);
      return;
    }
    let cancelled = false;
    setIsSearchLoading(true);
    runQuery(
      buildSearchFilters({
        search: debouncedSearch,
        scopeFilter,
        searchableFields,
      }),
      1
    )
      .then(res => {
        if (cancelled) return;
        setSearchTree(mapNodes(res.data).map(n => ({ ...n, isLeaf: true })));
      })
      .finally(() => {
        if (!cancelled) setIsSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, resetKey, searchableKey]);

  const getMeta = useCallback(
    (id: string): IOptionMeta | undefined => optionByIdRef.current.get(id),
    []
  );

  return {
    treeData: isSearching ? searchTree : browseTree,
    loadData,
    searchValue,
    setSearchValue,
    isLoading: isLoadingRoots || isSearchLoading,
    getMeta,
  };
};
